import * as React from 'react'
import Box from '@mui/material/Box'
import Stepper from '@mui/material/Stepper'
import Step from '@mui/material/Step'
import StepLabel from '@mui/material/StepLabel'
import Button from '@mui/material/Button'
import useMediaQuery from '@mui/material/useMediaQuery'
import useStyles from './_styles'
import PatientStep from './steps/PatientStep'
import {
    PlannedExpertiseStepperProps,
    PlannedExpertiseStepperStep,
    RequesterData,
    RequiredData,
    SelectedPatientType,
    SelectedWorkflowType,
} from './_props'
import ConsentStep from './steps/ConsentStep'
import { ArrowBackwardIcon, ArrowBackwardDisabledIcon } from '../../../assets/icons/icons'
import RequiredStep from './steps/RequiredStep'
import RequesterStep from './steps/RequesterStep'
import WorkflowStep from './steps/WorkflowStep'
import InitiationPreviewStep from './steps/InitiationPreviewStep'

const steps = ['Patient', 'Consentement', 'Requérant', 'Requis', 'Cible']

export default function PlannedExpertiseStepper({
    onComplete,
    onAbandon,
    onRecapChange,
    isLoading,
}: PlannedExpertiseStepperProps & { isLoading?: boolean }) {
    const classes = useStyles()
    const isStepperLoading = !!isLoading
    const isMobile = useMediaQuery('(max-width:700px)')
    const [activeStep, setActiveStep] = React.useState<number>(0)
    const [selectedPatient, setSelectedPatient] = React.useState<SelectedPatientType>(null)
    const [consentGiven, setConsentGiven] = React.useState<boolean | null>(null)
    const [selectedRequester, setSelectedRequester] = React.useState<RequesterData | null>(null)
    const [selectedRequired, setSelectedRequired] = React.useState<RequiredData | null>(null)
    const [selectedWorkflow, setSelectedWorkflow] = React.useState<SelectedWorkflowType>(null)
    const [isPatientDirty, setIsPatientDirty] = React.useState(false)
    const [editMode, setEditMode] = React.useState<boolean>(false)
    const patientBackupRef = React.useRef<{ patient: SelectedPatientType; consent: boolean | null } | null>(null)
    const isRecapStep = activeStep === steps.length

    React.useEffect(() => {
        if (!onRecapChange) return
        if (!selectedPatient || !selectedRequired) {
            onRecapChange(null)
            return
        }

        onRecapChange({
            patientFullName:
                selectedPatient.patientData?.fullName || selectedPatient.patientLibelle?.split(' (')[0] || 'Patient',
            patientBirthdate: selectedPatient.patientData?.birthDate || '—',
            patientSex: selectedPatient.patientData?.sex || null,
            patientId: selectedPatient.patientId,
            requesterId: selectedRequester?.requesterId,
            requiredExpertId: selectedRequired?.requiredExpertId,
            requiredExpertLabel: selectedRequired.requiredExpertLabel || '—',
            workflowId: selectedWorkflow?.id,
            workflowLabel: selectedWorkflow?.name || '—',
        })
    }, [onRecapChange, selectedPatient, selectedRequired, selectedRequester, selectedWorkflow])

    const isNextDisabled =
        (activeStep === PlannedExpertiseStepperStep.PATIENT && !selectedPatient) ||
        (activeStep === PlannedExpertiseStepperStep.CONSENT && consentGiven !== true) ||
        (activeStep === PlannedExpertiseStepperStep.REQUESTER && !selectedRequester) ||
        (activeStep === PlannedExpertiseStepperStep.REQUIRED && !selectedRequired) ||
        (activeStep === PlannedExpertiseStepperStep.WORKFLOW && !selectedWorkflow)
    const isBackDisabled =
        (activeStep === 0 && !editMode) ||
        (editMode && activeStep === PlannedExpertiseStepperStep.REQUESTER && !selectedRequester) ||
        (editMode && activeStep === PlannedExpertiseStepperStep.REQUIRED && !selectedRequired) ||
        (editMode && activeStep === PlannedExpertiseStepperStep.WORKFLOW && !selectedWorkflow)
    const backButtonLabel = editMode && activeStep !== PlannedExpertiseStepperStep.CONSENT ? 'Récapitulatif' : 'Retour'

    const handleNext = () => {
        if (activeStep < steps.length - 1) {
            setActiveStep((prevActiveStep) => prevActiveStep + 1)
        }
    }

    const handleBack = () => {
        if (editMode) {
            if (activeStep === PlannedExpertiseStepperStep.PATIENT) {
                if (patientBackupRef.current) {
                    setSelectedPatient(patientBackupRef.current.patient)
                    setConsentGiven(patientBackupRef.current.consent)
                }
                setEditMode(false)
                setActiveStep(steps.length)
                return
            }
            if (activeStep === PlannedExpertiseStepperStep.CONSENT) {
                setActiveStep(PlannedExpertiseStepperStep.PATIENT)
                setSelectedPatient(null)
                setConsentGiven(null)
                return
            }
            setEditMode(false)
            setActiveStep(steps.length)
            return
        }
        if (activeStep === PlannedExpertiseStepperStep.CONSENT) {
            setSelectedPatient(null)
            setConsentGiven(null)
        }
        if (activeStep === PlannedExpertiseStepperStep.REQUESTER) {
            setSelectedRequester(null)
            setConsentGiven(null)
        }
        if (activeStep === PlannedExpertiseStepperStep.REQUIRED) {
            setSelectedRequired(null)
        }
        if (activeStep > 0) {
            setActiveStep((prevActiveStep) => prevActiveStep - 1)
        }
    }

    const handleReset = () => {
        setActiveStep(0)
    }

    const handleAbandon = () => {
        onAbandon?.()
    }

    const handleRequesterChange = (requesterData: RequesterData) => {
        setSelectedRequester(requesterData)
    }

    const handleRequiredChange = (requiredData: RequiredData) => {
        setSelectedRequired(requiredData)
    }

    const handleWorkflowChange = (workflowData: { id: string; name: string }) => {
        setSelectedWorkflow(workflowData)
    }

    const handleComplete = () => {
        if (isRecapStep) {
            onComplete?.()
            return
        }
        if (editMode) {
            if (activeStep === PlannedExpertiseStepperStep.CONSENT) {
                setEditMode(false)
                setActiveStep(steps.length)
                return
            }
            if (activeStep === PlannedExpertiseStepperStep.REQUIRED) {
                setEditMode(false)
                setActiveStep(steps.length)
                return
            }
            if (activeStep !== PlannedExpertiseStepperStep.PATIENT) {
                setEditMode(false)
                setActiveStep(steps.length)
                return
            }
            handleNext()
            return
        }
        if (activeStep === steps.length - 1) {
            setActiveStep(steps.length)
            return
        }
        handleNext()
    }

    if (isStepperLoading) {
        return (
            <div className={classes.stepperContainer}>
                <div className={classes.stepperContent}>
                    <div className={classes.loadingHeader}></div>
                    <div className={classes.stepContainerLoading}></div>
                </div>
            </div>
        )
    }

    return (
        <div className={classes.stepperContainer}>
            <div className={classes.stepperContent}>
                <Box width="100%">
                    <Stepper activeStep={activeStep} alternativeLabel={isMobile} className={classes.stepper}>
                        {steps.map((label, index) => {
                            const isCompleted = editMode ? index !== activeStep : index < activeStep
                            return (
                                <Step key={label} completed={isCompleted}>
                                    <StepLabel>{label}</StepLabel>
                                </Step>
                            )
                        })}
                    </Stepper>

                    <div id="stepper-content">
                        {activeStep === steps.length ? (
                            <React.Fragment>
                                <div className={classes.stepContainer}>
                                    <InitiationPreviewStep
                                        selectedPatient={selectedPatient}
                                        selectedRequester={selectedRequester}
                                        selectedRequired={selectedRequired}
                                        selectedWorkflow={selectedWorkflow}
                                        isConsentValid={consentGiven === true}
                                        onEditPatient={() => {
                                            patientBackupRef.current = { patient: selectedPatient, consent: consentGiven }
                                            setActiveStep(PlannedExpertiseStepperStep.PATIENT)
                                            setSelectedPatient(null)
                                            setConsentGiven(null)
                                            setIsPatientDirty(true)
                                            setEditMode(true)
                                        }}
                                        onEditRequester={() => {
                                            setActiveStep(PlannedExpertiseStepperStep.REQUESTER)
                                            setEditMode(true)
                                        }}
                                        onEditRequired={() => {
                                            setActiveStep(PlannedExpertiseStepperStep.REQUIRED)
                                            setEditMode(true)
                                        }}
                                        onEditWorkflow={() => {
                                            setActiveStep(PlannedExpertiseStepperStep.WORKFLOW)
                                            setEditMode(true)
                                        }}
                                        onInit={handleComplete}
                                    />
                                </div>
                            </React.Fragment>
                        ) : (
                            <React.Fragment>
                                <div className={classes.stepContainer}>
                                    {(activeStep !== PlannedExpertiseStepperStep.PATIENT || editMode) && (
                                        <Button
                                            color="inherit"
                                            disabled={isBackDisabled}
                                            onClick={handleBack}
                                            className={classes.backButton}
                                        >
                                            {isBackDisabled ? (
                                                <ArrowBackwardDisabledIcon className={classes.disableBackIcon} />
                                            ) : (
                                                <ArrowBackwardIcon fontSize="small" />
                                            )}
                                            {backButtonLabel}
                                        </Button>
                                    )}
                                    <div className={classes.stepContent}>
                                        {activeStep === PlannedExpertiseStepperStep.PATIENT && (
                                            <PatientStep
                                                onDataChange={(data) => {
                                                    setSelectedPatient(data)
                                                    setConsentGiven(null)
                                                    setIsPatientDirty(true)
                                                }}
                                                onSelectAndContinue={handleComplete}
                                            />
                                        )}
                                        {activeStep === PlannedExpertiseStepperStep.CONSENT && (
                                            <ConsentStep
                                                patient={selectedPatient}
                                                consentGiven={consentGiven}
                                                onConsentChange={(value) => {
                                                    setConsentGiven(value)
                                                    if (value === true) {
                                                        handleComplete()
                                                    }
                                                }}
                                            />
                                        )}
                                        {activeStep === PlannedExpertiseStepperStep.REQUESTER && (
                                            <RequesterStep
                                                patient={selectedPatient}
                                                consentGiven={consentGiven}
                                                onConsentChange={(value) => setConsentGiven(value)}
                                                onDataChange={handleRequesterChange}
                                                onSelectAndContinue={handleComplete}
                                            />
                                        )}
                                        {activeStep === PlannedExpertiseStepperStep.REQUIRED && (
                                            <RequiredStep
                                                onDataChange={handleRequiredChange}
                                                onSelectAndContinue={handleComplete}
                                            />
                                        )}
                                        {activeStep === PlannedExpertiseStepperStep.WORKFLOW && (
                                            <WorkflowStep
                                                onDataChange={handleWorkflowChange}
                                                onSelectAndContinue={handleComplete}
                                            />
                                        )}
                                    </div>
                                </div>
                            </React.Fragment>
                        )}
                    </div>
                </Box>
            </div>
        </div>
    )
}
